import { AMOUNT_FORMAT } from 'components/xlsx/excelCells';
import { saveAs } from 'file-saver';

import { SalesJournalResponse, SalesJournalRow, VdRow } from 'types/reports';
import { EU_ISO3_TO_ISO2 } from './euCountryCodes';

/** Text number format forcing the VAT number cell to keep leading zeros instead of being read as a number. */
const TEXT_FORMAT = '@';

/** EMTA operation code for services in the VD filing. */
const SERVICES_OPERATION_CODE = 4;

/** Worksheet column index (1-based) of the net "Sales of services" column, used to build the total SUM. */
const NET_COLUMN_INDEX = 4;
const NET_COLUMN_LETTER = 'D';

/** Returns 0 for non-finite values (covers nulls coerced via the base-currency guard). */
const num = (value: number): number => (Number.isFinite(value) ? value : 0);

/**
 * Resolves a row's VD alpha-2 country code: the ISO3→ISO2 EU map when `countryCode` is present and known,
 * otherwise the VAT number's leading 2 letters uppercased (covers null/unmapped country codes).
 */
const resolveCountryCode = (countryCode: string | null, vatRegNum: string): string => {
    if (countryCode) {
        const mapped = EU_ISO3_TO_ISO2[countryCode.toUpperCase()];
        if (mapped) {
            return mapped;
        }
    }
    return vatRegNum.slice(0, 2).toUpperCase();
};

/** Strips a leading 2-letter alpha country prefix from a VAT number, preserving the rest incl. leading zeros. */
const stripVatPrefix = (vatRegNum: string): string => vatRegNum.replace(/^[A-Za-z]{2}/, '');

/** True for rows that belong in the VD filing: zero-rated intra-EU services to a VAT-registered EU buyer. */
const isVdRow = (row: SalesJournalRow): boolean =>
    row.countryLocationTypeCode === 'FOREIGN_EU' &&
    row.vatRegNum !== '' &&
    num(row.baseCurrency?.invoice.vat ?? 0) === 0;

/**
 * Filters the journal rows to the VD set, then re-merges them by (alpha-2 country, stripped VAT number).
 * Per group the net sums `baseCurrency.invoice.totalWithoutVat` as-is — backend totals are already signed,
 * so credit notes carry negative amounts. Invoices are counted, and the customer name is taken from any
 * contributing row. Sorted by country then stripped VAT number.
 */
export const buildVdRows = (rows: SalesJournalRow[]): VdRow[] => {
    const groups = new Map<string, VdRow>();

    rows.filter(isVdRow).forEach((row) => {
        const countryCode = resolveCountryCode(row.countryCode, row.vatRegNum);
        const vatNumber = stripVatPrefix(row.vatRegNum);
        const key = `${countryCode}|${vatNumber}`;
        const net = num(row.baseCurrency?.invoice.totalWithoutVat ?? 0);

        const existing = groups.get(key);
        if (existing) {
            existing.netAmount += net;
            existing.invoiceCount += 1;
        } else {
            groups.set(key, {
                countryCode,
                vatNumber,
                customerName: row.clientPartyName,
                netAmount: net,
                invoiceCount: 1,
            });
        }
    });

    return Array.from(groups.values()).sort(
        (a, b) => a.countryCode.localeCompare(b.countryCode) || a.vatNumber.localeCompare(b.vatNumber)
    );
};

/** Formats a 'yyyy-MM' reporting month as MM/YYYY for the header block. */
const formatTargetMonth = (reportingMonth: string): string => {
    const [year, month] = reportingMonth.split('-');
    if (!year || !month) {
        return reportingMonth;
    }
    return `${month}/${year}`;
};

/**
 * Builds the single-sheet "VD" workbook (VAT summary statement) from the sales-journal response and
 * triggers a download. The net total is written as a live SUM formula over the data rows so the line-101
 * total always reconciles with the per-group nets.
 */
export const exportForeignSalesXlsx = async (
    response: SalesJournalResponse,
    reportingMonth: string,
    businessId: string
): Promise<void> => {
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    // The line-101 total caches its exact JS-computed sum so a non-recalculating spreadsheet app shows the
    // correct value; this flag additionally asks recalculating apps to refresh it on open.
    workbook.calcProperties.fullCalcOnLoad = true;
    const sheet = workbook.addWorksheet('VD');

    sheet.columns = [{ width: 16 }, { width: 22 }, { width: 32 }, { width: 22 }, { width: 10 }];

    sheet.getCell('A1').value = `VAT SUMMARY STATEMENT / VD aruanne ${reportingMonth}`;
    sheet.getCell('A1').font = { bold: true };

    sheet.getCell('A2').value = 'Business ID';
    sheet.getCell('B2').value = businessId;
    sheet.getCell('C2').value = '010';

    sheet.getCell('A3').value = 'Target month';
    sheet.getCell('B3').value = formatTargetMonth(reportingMonth);

    const headerRow = sheet.getRow(5);
    ['Country Code', 'Customer VAT Number', 'Customer Name', 'Sales of services (net, EUR)', 'Code'].forEach(
        (label, index) => {
            headerRow.getCell(index + 1).value = label;
        }
    );
    headerRow.font = { bold: true };

    const vdRows = buildVdRows(response.invoices);
    const dataFirst = 6;
    const dataLast = dataFirst + vdRows.length - 1;

    vdRows.forEach((vdRow, index) => {
        const sheetRow = sheet.getRow(dataFirst + index);
        sheetRow.getCell(1).value = vdRow.countryCode;

        const vatCell = sheetRow.getCell(2);
        vatCell.value = vdRow.vatNumber;
        vatCell.numFmt = TEXT_FORMAT;

        sheetRow.getCell(3).value = vdRow.customerName;

        const netCell = sheetRow.getCell(NET_COLUMN_INDEX);
        netCell.value = vdRow.netAmount;
        netCell.numFmt = AMOUNT_FORMAT;

        sheetRow.getCell(5).value = SERVICES_OPERATION_CODE;
    });

    // The total lands on the row after the data; with no data rows it sits on dataFirst and carries a literal 0,
    // since a SUM over the collapsed range would otherwise reference the total cell itself (a circular reference).
    const totalRowNumber = Math.max(dataLast + 1, dataFirst);
    const totalRow = sheet.getRow(totalRowNumber);
    totalRow.getCell(1).value = 'Total / Kokku';
    totalRow.getCell(3).value = '101';
    const totalNetCell = totalRow.getCell(NET_COLUMN_INDEX);
    const totalNet = vdRows.reduce((sum, vdRow) => sum + vdRow.netAmount, 0);
    totalNetCell.value =
        vdRows.length === 0
            ? 0
            : { formula: `SUM(${NET_COLUMN_LETTER}${dataFirst}:${NET_COLUMN_LETTER}${dataLast})`, result: totalNet };
    totalNetCell.numFmt = AMOUNT_FORMAT;
    totalRow.font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `VD ${reportingMonth}.xlsx`);
};
